import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isAdminEmail } from '../lib/adminAllowlist';
import UtilityPageFrame from './utility/UtilityPageFrame';

const MAX_EMAIL_LENGTH = 120;
const MAX_PASSWORD_LENGTH = 128;

const removeControlCharacters = (input: string) =>
  Array.from(input)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('');

const sanitizeEmailField = (input: string) =>
  removeControlCharacters(input)
    .replace(/\s+/g, '')
    .slice(0, MAX_EMAIL_LENGTH);

const sanitizePasswordField = (input: string) =>
  removeControlCharacters(input).slice(0, MAX_PASSWORD_LENGTH);

const RESET_COOLDOWN_MS = 60000; // 1 minute between reset requests

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const forgotButtonRef = useRef<HTMLButtonElement>(null);
  const resetEmailRef = useRef<HTMLInputElement>(null);
  const resetDialogRef = useRef<HTMLDivElement>(null);

  // Dialog behavior: initial focus into the email field, Escape/backdrop to
  // close, Tab containment, background scroll lock, and focus returned to the
  // "Forgot password?" button.
  useEffect(() => {
    if (!showForgotPassword) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const trigger = forgotButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    resetEmailRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowForgotPassword(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = resetDialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (el) => !el.hasAttribute('disabled') && !el.closest('[hidden], [aria-hidden="true"]')
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!active || !focusables.includes(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      const target =
        previousFocus && previousFocus !== document.body && document.contains(previousFocus)
          ? previousFocus
          : trigger;
      target?.focus({ preventScroll: true });
    };
  }, [showForgotPassword]);

  const MAX_ATTEMPTS = 5;
  const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Check for rate limiting
    const lastAttemptTime = localStorage.getItem('lastLoginAttempt');
    const currentAttempts = parseInt(localStorage.getItem('loginAttempts') || '0');
    
    if (currentAttempts >= MAX_ATTEMPTS && lastAttemptTime) {
      const timeDiff = Date.now() - parseInt(lastAttemptTime);
      if (timeDiff < LOCKOUT_TIME) {
        const remainingTime = Math.ceil((LOCKOUT_TIME - timeDiff) / 60000);
        setError(`Too many failed attempts. Please try again in ${remainingTime} minutes.`);
        return;
      } else {
        // Reset attempts after lockout period
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('lastLoginAttempt');
      }
    }

    const sanitizedEmail = sanitizeEmailField(credentials.email.trim().toLowerCase());
    const sanitizedPassword = sanitizePasswordField(credentials.password);

    // Input validation
    if (!sanitizedEmail || !sanitizedPassword.trim()) {
      setError('Please enter both email and password.');
      return;
    }

    // Keep local state aligned with sanitized values
    setCredentials({ email: sanitizedEmail, password: sanitizedPassword });
    setLoading(true);
    
    try {
      const result = await login(sanitizedEmail, sanitizedPassword);

      if (result === 'success') {
        // Reset failed attempts on successful login
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('lastLoginAttempt');
        setCredentials({ email: '', password: '' });
        navigate('/admin');
      } else if (result === 'denied') {
        // Auth succeeded but the account is not in the admin allowlist.
        // Give a clear, calm access-denied message rather than "invalid credentials".
        setError('Access denied: this account does not have admin access.');
        setCredentials(prev => ({ ...prev, password: '' }));
      } else {
        const newAttempts = currentAttempts + 1;
        localStorage.setItem('loginAttempts', newAttempts.toString());
        localStorage.setItem('lastLoginAttempt', Date.now().toString());

        if (newAttempts >= MAX_ATTEMPTS) {
          setError('Maximum login attempts exceeded. Account locked for 15 minutes.');
        } else {
          setError(`Invalid credentials. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`);
        }
        // Clear password field for security
        setCredentials(prev => ({ ...prev, password: '' }));
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = name === 'email' ? sanitizeEmailField(value) : sanitizePasswordField(value);
    setCredentials(prev => ({ ...prev, [name]: sanitizedValue }));
    if (error) setError('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage(null);

    const email = sanitizeEmailField(resetEmail.trim().toLowerCase());
    if (!email) {
      setResetMessage({ text: 'Please enter your email address.', type: 'error' });
      return;
    }
    if (!isAdminEmail(email)) {
      setResetMessage({ text: 'Only approved admin accounts can reset passwords here.', type: 'error' });
      return;
    }

    // Rate limit: 1 request per 60 seconds
    const lastReset = localStorage.getItem('lastPasswordReset');
    if (lastReset) {
      const elapsed = Date.now() - parseInt(lastReset);
      if (elapsed < RESET_COOLDOWN_MS) {
        const remaining = Math.ceil((RESET_COOLDOWN_MS - elapsed) / 1000);
        setResetMessage({ text: `Please wait ${remaining} seconds before requesting another reset.`, type: 'error' });
        return;
      }
    }

    setResetLoading(true);
    try {
      const redirectUrl = `${window.location.origin}/auth/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
      if (error) throw error;

      localStorage.setItem('lastPasswordReset', Date.now().toString());
      setResetMessage({
        text: `Password reset link sent to ${email}. Check your inbox (and spam folder).`,
        type: 'success',
      });
    } catch {
      setResetMessage({ text: 'Could not send the reset email. Please try again later.', type: 'error' });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <UtilityPageFrame title="Admin access" context={
      <p className="utility-muted">
        Sign in with an approved admin account to manage RidePulse site content
        and operations.
      </p>
    }>
      <div className="utility-card">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium" style={{ color: 'var(--rp-text)' }}>
                Admin email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" style={{ color: 'var(--rp-text-tertiary)' }}>
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="off"
                  required
                  value={credentials.email}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full pl-10 px-3 py-2 border rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--rp-border)',
                    backgroundColor: 'var(--rp-surface-secondary)',
                    color: 'var(--rp-text)'
                  }}
                  placeholder="Enter your email"
                  disabled={loading}
                  maxLength={MAX_EMAIL_LENGTH}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--rp-text)' }}>
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" style={{ color: 'var(--rp-text-tertiary)' }}>
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="off"
                  required
                  value={credentials.password}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full pl-10 px-3 py-2 border rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--rp-border)',
                    backgroundColor: 'var(--rp-surface-secondary)',
                    color: 'var(--rp-text)'
                  }}
                  placeholder="Enter password"
                  disabled={loading}
                  maxLength={MAX_PASSWORD_LENGTH}
                />
              </div>
            </div>

            <div aria-live="assertive">
              {error && (
                <div className="utility-message utility-message--error" role="alert">
                  {error}
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                ref={forgotButtonRef}
                onClick={() => { setShowForgotPassword(true); setError(''); }}
                className="text-sm hover:underline transition-colors"
                style={{ color: 'var(--rp-text-secondary)' }}
              >
                Forgot password?
              </button>
            </div>
          </form>
        </div>
        <div className="text-center text-xs" style={{ color: 'var(--rp-text-tertiary)', marginTop: '1rem' }}>
          <p>This is a protected area. Unauthorized access is prohibited.</p>
        </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowForgotPassword(false)}>
          <div ref={resetDialogRef} role="dialog" aria-modal="true" aria-label="Reset password" className="w-full max-w-md rounded-xl p-8" style={{ backgroundColor: 'var(--rp-surface)', border: '1px solid var(--rp-border)' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold" style={{ color: 'var(--rp-text)' }}>Reset Password</h3>
              <button onClick={() => setShowForgotPassword(false)} aria-label="Close" className="text-xl" style={{ color: 'var(--rp-text-secondary)' }}>×</button>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              Enter your admin email address. We'll send you a link to reset your password.
            </p>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium mb-1" style={{ color: 'var(--rp-text)' }}>
                  Admin email
                </label>
                <input
                  id="reset-email"
                  ref={resetEmailRef}
                  type="email"
                  value={resetEmail}
                  onChange={e => { setResetEmail(sanitizeEmailField(e.target.value)); setResetMessage(null); }}
                  placeholder="you@ridepulse.app"
                  className="w-full px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--rp-border)', backgroundColor: 'var(--rp-surface-secondary)', color: 'var(--rp-text)' }}
                  disabled={resetLoading}
                  maxLength={MAX_EMAIL_LENGTH}
                />
              </div>

              <div aria-live="polite">
                {resetMessage && (
                  <div className={`utility-message ${resetMessage.type === 'error' ? 'utility-message--error' : 'utility-message--success'}`} role={resetMessage.type === 'error' ? 'alert' : 'status'}>
                    {resetMessage.text}
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="px-4 py-2 rounded-lg text-sm"
                  style={{ color: 'var(--rp-text-secondary)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </UtilityPageFrame>
  );
};

export default Login;
